import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { formatMonth, Flag } from '@laboratoria/sdk-react';
import webDevImg from '../../assets/web-dev.jpg';
import uxImg from '../../assets/ux.jpg';
import dataImg from '../../assets/track-data.png';

const coverImages = {
  'web-dev': webDevImg,
  ux: uxImg,
  data: dataImg,
};

const Program = ({
  lang,
  program,
  countriesByCode,
  programCount,
}) => {
  const { formatMessage } = useIntl();
  const slug = program.track.name;
  const cover = coverImages[slug];
  const title = `home/${slug}-title`;
  const descriptionId = `home/${slug}-description`;

  const containerSx = {
    marginBottom: '40px',
    display: 'flex',
    flexDirection: 'column',
    paddingX: {
      xs: '0px',
      md: '16px',
    },
    img: {
      borderRadius: '0.625rem',
      marginBottom: '40px',
      objectFit: 'cover',
      objectPosition: '50% 50%',
      height: '100%',
      width: '100%',
    },
    '& .MuiTypography-h3': {
      fontWeight: '600',
      fontSize: {
        xs: '1.375rem',
        sm: '1.625rem',
        md: '2rem',
      },
    },
    a: {
      textDecoration: 'none',
      ':hover': {
        color: '#FB5EBF',
      },
    },
    '& .MuiContainer-root': {
      paddingX: 0,
    },
    '& .MuiBox-root': {
      backgroundColor: '#F2F2F2',
      padding: '15px',
      borderRadius: '10px',
      marginBottom: '35px',
      ul: {
        listStyleType: 'none',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        paddingLeft: '0px',
      },
      li: {
        alignItem: 'center',
      },
      '& .MuiTypography-textBox': {
        display: 'inline',
        fontSize: '1.063rem',
        fontWeight: '400',
        marginRight: '15px',
      },
    },
    '& .MuiAlert-icon': {
      color: '#000000',
    },
    '& .MuiTypography-tag': {
      fontSize: { xs: '0.75rem', md: '0.938rem' }
    },
  };

  let gridItemSm = 12;
  if (programCount < 2) {
    gridItemSm = 6;
    Object.assign(containerSx, {
      flexDirection: { xs: 'column', sm: 'row' },
      paddingX: {
        ...containerSx.paddingX,
        md: 0,
      },
    });
  }

  return (
    <Container sx={containerSx}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={gridItemSm}>
          <Link to={slug}>
            <img src={cover} alt={title} />
          </Link>
        </Grid>
        <Grid item sm={gridItemSm}>
          <Link to={slug}>
            <Typography variant="h3"
              sx={{
                cursor: 'pointer',
              }}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: formatMessage({ id: title }),
                }}
              ></span>
            </Typography>
          </Link>
          <Typography
            variant="body1"
            sx={{
              marginBottom: 3,
              marginTop: { xs: 3, sm: 4 },
            }}>
            <FormattedMessage id={descriptionId} />
          </Typography>
          <Box>
            <Typography
              sx={{
                fontSize: '1.125rem',
                fontWeight: '600',
              }}>
              <span
                dangerouslySetInnerHTML={{
                  __html: formatMessage({ id: 'home/next-bootcamps' }),
                }}
              ></span>
            </Typography>
            {!!program?.cohorts?.length
              ? (
                <ul>
                  {program.cohorts.map(cohort => {
                    const admissionCountries = cohort.admissionCountries.filter(
                      code => countriesByCode[code]?.canApply,
                    );
                    return (
                      <li key={cohort.id}>
                        <CalendarTodayOutlinedIcon
                          fontSize="inherit"
                          sx={{
                            display: 'inline',
                            width: '22px',
                            marginRight: '5px',
                          }}
                        />
                        <Typography variant="textBox">
                          {formatMonth(cohort.start, lang, true).replace(
                            /de (\w)/g,
                            (_, letter) => `de ${letter.toUpperCase()}`,
                          )}
                          <span
                            style={(
                              admissionCountries.length < 5
                                ? { display: 'inline-block', margin: '0 0 0 5px' }
                                : { display: 'block', margin: 0 }
                            )}
                          >
                            {'('}
                            {admissionCountries.map(code => (
                              <Flag key={code} country={countriesByCode[code]} />
                            ))}
                            {')'}
                          </span>
                        </Typography>
                      </li>
                    );
                  })}
                </ul>)
              : (
                <Typography
                  sx={{
                    marginY: '16px',
                    textTransform: 'none',
                  }}>
                  <FormattedMessage id="home/no-available-cohorts" />
                </Typography>
              )
            }
          </Box>
          <Button size="large" color="secondary" component={Link} to={slug}>
            <FormattedMessage id="home/know-more" />
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Program;
